export type VoiceSampleIdentity = {
  id: string; display_name: string; sample_audio_url: string | null;
  provider_metadata?: Record<string, unknown> | null;
};
export type CatalogVoiceReview = {
  voice_id: string; verified_at: string; sample_audio_url: string; sample_sha256: string | null;
};
export type CatalogVoiceReviewClear = Omit<CatalogVoiceReview, 'verified_at'> & { cleared_through: string; cleared_at: string };
export const sampleHash = (voice: VoiceSampleIdentity): string | null =>
  typeof voice.provider_metadata?.sample_sha256 === 'string' ? voice.provider_metadata.sample_sha256 : null;
// PostgreSQL timestamps can differ below JavaScript's millisecond precision.
function compareReviewTimes(left: string, right: string): number {
  const milliseconds = Date.parse(left) - Date.parse(right);
  if (milliseconds) return milliseconds;
  const fraction = (value: string) => (value.match(/\.(\d+)(?:Z|[+-]\d{2}(?::?\d{2})?)$/)?.[1] ?? '').padEnd(9, '0').slice(3, 9);
  return fraction(left).localeCompare(fraction(right));
}

export function readCatalogVoiceReview(value: unknown): CatalogVoiceReview {
  const row = value as Partial<CatalogVoiceReview> | null;
  if (!row || typeof row.voice_id !== 'string' || typeof row.verified_at !== 'string' ||
    !Number.isFinite(Date.parse(row.verified_at)) || typeof row.sample_audio_url !== 'string' ||
    (row.sample_sha256 !== null && typeof row.sample_sha256 !== 'string')) throw Error('Invalid verification response');
  return row as CatalogVoiceReview;
}
export function reviewMatchesSample(review: CatalogVoiceReview | undefined, voice: VoiceSampleIdentity): boolean {
  return !!review && review.voice_id === voice.id && review.sample_audio_url === voice.sample_audio_url && review.sample_sha256 === sampleHash(voice);
}
export function readCatalogVoiceReviewClear(value: unknown): CatalogVoiceReviewClear {
  const row = value as Partial<CatalogVoiceReviewClear> | null;
  if (!row || typeof row.cleared_at !== 'string' || !Number.isFinite(Date.parse(row.cleared_at))) throw Error('Invalid clear response');
  readCatalogVoiceReview({ ...row, verified_at: row.cleared_through });
  return row as CatalogVoiceReviewClear;
}
export function reviewWasCleared(review: CatalogVoiceReview, cleared: CatalogVoiceReviewClear | undefined): boolean {
  return !!cleared && cleared.voice_id === review.voice_id && cleared.sample_audio_url === review.sample_audio_url &&
    cleared.sample_sha256 === review.sample_sha256 && compareReviewTimes(review.verified_at, cleared.cleared_through) <= 0;
}
export function mergeCatalogVoiceReviews(previous: Map<string, CatalogVoiceReview>, incoming: CatalogVoiceReview[], clears?: Map<string, CatalogVoiceReviewClear>): Map<string, CatalogVoiceReview> {
  const next = new Map(previous);
  for (const review of incoming) {
    if (reviewWasCleared(review, clears?.get(review.voice_id))) continue;
    const current = next.get(review.voice_id);
    if (!current || compareReviewTimes(review.verified_at, current.verified_at) >= 0) next.set(review.voice_id, review);
  }
  return next;
}
export function clearCatalogVoiceReviewLocally(previous: Map<string, CatalogVoiceReview>, cleared: CatalogVoiceReviewClear): Map<string, CatalogVoiceReview> {
  const next = new Map(previous), current = next.get(cleared.voice_id);
  if (current && reviewWasCleared(current, cleared)) next.delete(cleared.voice_id);
  return next;
}
export function auditionPageKey(voices: readonly VoiceSampleIdentity[]): string {
  // Label edits keep the review and the resume position. Replacing a sample or
  // changing the current page/order cancels the old playback queue.
  return JSON.stringify(voices.map(v => [v.id, v.sample_audio_url, sampleHash(v)]));
}
